import { useCallback, useMemo } from "react";
import { SetURLSearchParams } from "react-router-dom";
import {
  DISPLAY_UNIT_DEFAULTS,
  DISPLAY_UNIT_QUERY_KEYS,
  DisplayUnitMetric,
  DisplayUnits,
} from "./definitions";
import { normalizeDisplayUnits } from "./converters";

type WritableMetric = Exclude<DisplayUnitMetric, "efficiency">;

type SearchParamValue = string | number | undefined;

const buildNextParams = (
  current: URLSearchParams,
  values: Partial<Record<string, SearchParamValue>>
) => {
  const next = new URLSearchParams(current);

  Object.entries(values).forEach(([key, value]) => {
    if (value === undefined || value === "") {
      next.delete(key);
      return;
    }

    next.set(key, String(value));
  });

  return next;
};

const readDisplayUnits = (searchParams: URLSearchParams): DisplayUnits =>
  normalizeDisplayUnits({
    flow: searchParams.get(DISPLAY_UNIT_QUERY_KEYS.flow),
    head: searchParams.get(DISPLAY_UNIT_QUERY_KEYS.head),
    power: searchParams.get(DISPLAY_UNIT_QUERY_KEYS.power),
    npshr: searchParams.get(DISPLAY_UNIT_QUERY_KEYS.npshr),
    diameter: searchParams.get(DISPLAY_UNIT_QUERY_KEYS.diameter),
    viscosity: searchParams.get(DISPLAY_UNIT_QUERY_KEYS.viscosity),
    efficiency: DISPLAY_UNIT_DEFAULTS.efficiency,
  });

const useDisplayUnits = (
  searchParams: URLSearchParams,
  setSearchParams: SetURLSearchParams
) => {
  const displayUnits = useMemo(
    () => readDisplayUnits(searchParams),
    [searchParams.toString()]
  );

  const updateDisplayUnit = useCallback(
    (metric: WritableMetric, unit: DisplayUnits[WritableMetric]) => {
      const next = buildNextParams(searchParams, {
        [DISPLAY_UNIT_QUERY_KEYS[metric]]: unit,
      });
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  const setParamsWithDisplayUnits = useCallback(
    (values: Partial<Record<string, SearchParamValue>>) => {
      const next = buildNextParams(new URLSearchParams(), {
        ...values,
        [DISPLAY_UNIT_QUERY_KEYS.flow]: displayUnits.flow,
        [DISPLAY_UNIT_QUERY_KEYS.head]: displayUnits.head,
        [DISPLAY_UNIT_QUERY_KEYS.power]: displayUnits.power,
        [DISPLAY_UNIT_QUERY_KEYS.npshr]: displayUnits.npshr,
        [DISPLAY_UNIT_QUERY_KEYS.diameter]: displayUnits.diameter,
        [DISPLAY_UNIT_QUERY_KEYS.viscosity]: displayUnits.viscosity,
      });
      setSearchParams(next);
    },
    [displayUnits, setSearchParams]
  );

  return {
    displayUnits,
    updateDisplayUnit,
    setParamsWithDisplayUnits,
  };
};

export default useDisplayUnits;
